<template>
  <section id="create-pro-password">
    <div
      v-if="loading"
      class="l-loading-container"
    >
      <img
        src="@shared/assets/img/general/loading.svg"
        alt="Loading"
      >
    </div>
    <div
      v-if="!loading"
      class="l-left-container"
    >
      <img
        alt="l-image"
        class="l-image"
        src="@shared/assets/img/logo/smac-sensors.png"
      >
      <div class="l-slogan">
        <div>CHANGE IS</div>
        <div class="l-tm-container">
          IN THE AIR<span class="l-tm">&reg;</span>
        </div>
      </div>
    </div>
    <div
      v-if="!loading"
      class="l-right-container"
    >
      <div class="l-header-container">
        <div class="flex-column">
          <div class="l-header">
            Welcome!
          </div>
          <div class="l-sub-header">
            Create an Account
          </div>
        </div>
        <img
          class="pro-logo"
          src="@shared/assets/img/general/pro-app-logo.svg"
          alt="PRO App"
        >
      </div>
      <div
        v-if="errorObj.createPasswordErr"
        class="l-login-error"
      >
        {{ errorObj.createPasswordErr }}
      </div>
      <form
        class="l-form-container"
        @submit.prevent="createPassword"
      >
        <div class="l-input-container">
          <div class="l-label-container">
            <label
              for="fullName"
              class="l-label"
            >
              Full Name
            </label>
          </div>
          <input
            id="fullName"
            v-model="fullName"
            type="text"
            class="l-input"
            placeholder="Enter full name"
          >
          <div
            v-if="errorObj.fullNameErr"
            class="error-text"
          >
            {{ errorObj.fullNameErr }}
          </div>
        </div>
        <div class="l-input-container">
          <div class="l-label-container">
            <label
              for="phone"
              class="l-label"
            >
              Phone Number
            </label>
          </div>
          <input
            id="phone"
            type="text"
            class="l-input"
            :value="phone"
            placeholder="Enter phone number"
            @input="handleChange('phone', $event.target.value)"
            @keypress="isPhoneNumber"
          >
          <div
            v-if="errorObj.emailErr"
            class="error-text"
          >
            {{ errorObj.emailErr }}
          </div>
        </div>
        <div class="l-input-container">
          <div class="l-label-container">
            <label
              for="password"
              class="l-label"
            >
              Create Password
            </label>
          </div>
          <div class="l-input-box-container">
            <input
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="l-input"
              placeholder="8 characters, a capital letter, and a number"
            >
            <button
              class="l-show-button"
              @click.prevent="showPassword = !showPassword"
            >
              <img
                v-if="!showPassword"
                alt="show-pass"
                src="@shared/assets/img/general/show-password.png"
              >
              <img
                v-else
                alt="hide-pass"
                src="@shared/assets/img/general/hide-password.png"
              >
            </button>
          </div>
          <div
            v-if="errorObj.passwordErr"
            class="error-text"
          >
            {{ errorObj.passwordErr }}
          </div>
        </div>
        <div class="l-input-container">
          <div class="l-label-container">
            <label
              for="confirmPassword"
              class="l-label"
            >
              Confirm Password
            </label>
          </div>
          <div class="l-input-box-container">
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="l-input"
              placeholder="8 characters, a capital letter, and a number"
            >
            <button
              class="l-show-button"
              @click.prevent="showConfirmPassword = !showConfirmPassword"
            >
              <img
                v-if="!showConfirmPassword"
                alt="show-pass"
                src="@shared/assets/img/general/show-password.png"
              >
              <img
                v-else
                alt="hide-pass"
                src="@shared/assets/img/general/hide-password.png"
              >
            </button>
          </div>
          <div
            v-if="errorObj.confirmPasswordErr"
            class="error-text"
          >
            {{ errorObj.confirmPasswordErr }}
          </div>
        </div>
        <div class="l-checkbox-container">
          <div class="flex-row align-center">
            <input
              id="term-checkbox"
              v-model="terms"
              type="checkbox"
              class="l-checkbox"
            >
            <label
              for="term-checkbox"
              class="l-label"
            >
              I agree to the SmartAC.com <a href="https://smartac.com/term">Terms of Service</a>
            </label>
          </div>
          <div class="error-text">
            {{ errorObj.termsErr }}
          </div>
        </div>
        <button
          class="l-button"
          type="submit"
        >
          <span v-if="!processing">CREATE ACCOUNT</span>
          <img
            v-else
            alt="loading"
            src="@shared/assets/img/general/loading-white.svg"
            class="spinner"
          >
        </button>
      </form>
    </div>
  </section>
</template>

<script>
import apiClient from '@shared/lib/general/apiClient';
import parseJwt from '@shared/lib/general/parseJwt';
import formatPhoneNumber from '@shared/lib/formatter/formatPhoneNumber';
import phoneValidation from '@shared/lib/errorCatch/phoneValidation';
import passwordValidation from '@shared/lib/errorCatch/passwordValidation';
import formatterMixin from '@shared/mixins/formatterMixin';

export default {
  name: 'CreateProPasswordView',
  metaInfo: {
    title: 'SmartAC.com | Create Password',
  },
  mixins: [formatterMixin],
  data() {
    return {
      fullName: '',
      phone: '',
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      terms: false,
      technician: {},
      errorObj: {},
      processing: false,
      loading: true,
    };
  },
  mounted() {
    const {
      token,
    } = this.$route.params;

    try {
      const decoded = parseJwt(token);
      const { exp } = decoded;

      if (Date.now() >= exp * 1000) {
        this.$router.replace('/expired-link');
      } else {
        localStorage.currentUser = JSON.stringify({ token });
        this.getTechnicianInfo();
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  },
  methods: {
    handleChange(field, value) {
      let v = value;
      if (field === 'phone') {
        v = formatPhoneNumber(v);
      }
      this[field] = v;
    },
    async createPassword() {
      const {
        fullName,
        phone,
        password,
        confirmPassword,
        terms,
      } = this;

      const trimmedFullName = fullName.trim();
      const trimmedPhone = phone.trim();

      const error = {};
      if (!trimmedFullName) {
        error.fullNameErr = 'Please enter your full name';
      } else if (trimmedFullName.split(' ').length < 2) {
        error.fullNameErr = 'Please enter your first and last name';
      }
      if (!trimmedPhone) {
        error.phoneErr = 'Please enter your phone number';
      } else if (!phoneValidation(trimmedPhone)) {
        error.phoneErr = 'Phone number is not valid';
      }
      if (!password) {
        error.passwordErr = 'Please enter password';
      } else if (passwordValidation(password).length > 0) {
        error.passwordErr = 'Please include 8 characters, a capital letter, and a number.';
      }
      if (!confirmPassword) {
        error.confirmPasswordErr = 'Please enter confirm password';
      } else if (password !== confirmPassword) {
        error.confirmPasswordErr = 'Confirm password does not match password';
      }
      if (!terms) {
        error.termsErr = 'Please accept Terms of Service';
      }

      this.errorObj = error;

      if (Object.keys(error).length === 0) {
        try {
          this.processing = true;

          const [firstName, lastName] = trimmedFullName.split(' ');

          const updateTech = {
            ...this.technician,
            firstName,
            lastName,
            phone: trimmedPhone,
            password,
            hasLoggedIn: true,
            createPassword: false,
            accountStatus: 'Active',
          };

          const { data } = await apiClient({
            method: 'PUT',
            url: '/auth/technician/change-password',
            data: updateTech,
          });

          if (data) {
            this.$router.replace('/pro/success');
          }
        } catch (err) {
          if (err.response) {
            const { data } = err.response;
            this.errorObj = {
              createPasswordErr: data.message,
            };
          }
        }
      }
      this.processing = false;
    },
    async getTechnicianInfo() {
      this.loading = true;
      try {
        const { data } = await apiClient({
          method: 'GET',
          url: '/technicians/info',
        });
        if (data && data.id) {
          const {
            firstName,
            lastName,
            phone,
            accountStatus,
            createPassword,
          } = data;
          if (accountStatus.toLowerCase().includes('deactivated') || !createPassword) {
            this.$router.replace('/expired-link');
          }
          this.fullName = `${firstName} ${lastName}`;
          this.phone = phone;
          this.technician = data;
        } else {
          this.$router.replace('/expired-link');
        }
      } catch (err) {
        this.$router.replace('/expired-link');
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss">

section#create-pro-password {
  display: flex;
  width: 100%;
  height: 100%;
  background-color: $WHITE;

  .l-loading-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .l-left-container {
    background-color: $N110;
    flex: 1;
    min-height: 722px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    justify-content: center;

    @media (max-width: 768px) {
      display: none;
    }

    .l-image {
      width: 50%;
      margin-bottom: 50px;

      @media (max-width: 1440px) {
        width: 70%;
      }
    }

    .l-comfort-tm {
      position: absolute;
      bottom: 27px;
      color: $WHITE;
      font-size: 2rem;
      font-weight: 500;
      line-height: 3rem;
      right: 20px;

      .l-sm-tm {
        color: $N80;
        font-weight: bold;
      }
    }

    .l-slogan {
      width: 50%;
      font-size: 4.8125rem;
      color: $WHITE;
      font-weight: 800;
      line-height: 5rem;

      .l-tm-container {
        position: relative;

        .l-tm {
          font-size: 1rem;
          position: absolute;
          top: -24px;
        }
      }

      @media (max-width: 1550px) {
        font-size: 3.8rem;

        .l-tm-container {
          .l-tm {
            top: -15px;
          }
        }
      }

      @media (max-width: 1440px) {
        width: 70%;
      }
    }
  }

  .l-right-container {
    min-width: 680px;
    min-height: 722px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 136px;

    @media (max-width: 768px) {
      padding: 0 100px;
      width: 100%;
      min-width: 0;
      min-height: 0;
    }

    @media (max-width: 420px) {
      padding: 0 40px;
    }

    @media (max-width: 320px) {
      padding: 0 25px;
    }

    .l-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6vh;

      .l-header {
        font-size: 3.375rem;
        color: $N100;
      }

      .l-sub-header {
        font-size: 1.5rem;
        color: $N100;
      }

      .pro-logo {
        height: 100%;
      }
    }

    .l-form-container {
      .l-input-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-bottom: 2vh;

        .l-label-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;

          .l-label {
            font-size: 1rem;
            color: $N100;
            font-weight: 500;
            text-transform: uppercase;
          }
        }

        .l-input-box-container {
          position: relative;

          .l-input {
            width: 100%;
          }

          .l-show-button {
            position: absolute;
            padding-right: 9px;
            right: 2px;
            top: 2px;
            bottom: 2px;
            background-color: $WHITE;
            display: flex;
            align-items: center;

            img {
              height: 24px;
            }
          }
        }

        .l-input {
          height: 56px;
          border: 2px solid $N10;
          border-radius: 4px;
          padding-left: 12px;
          font-size: 1.25rem;

          &:focus {
            border: 2px solid $NBLUE80;
          }

          @media (max-width: 320px) {
            height: 40px;
          }
        }

        .error-text {
          font-size: 0.75rem;
          color: $TOMATO100;
        }
      }

      .l-checkbox-container {
        display: flex;
        flex-direction: column;
        margin-top: 5vh;
        margin-bottom: 2vh;

        .l-checkbox {
          height: 24px;
          width: 24px;
        }

        .l-label {
          margin-left: 8px;

          a {
            font-weight: 700;
            color: $NBLUE100;
          }
        }

        .error-text {
          font-size: 0.75rem;
          color: $TOMATO100;
        }
      }

      .l-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 56px;
        background-color: $NBLUE100;
        border-radius: 6px;
        color: $WHITE;
        font-size: 1rem;
        font-weight: 600;

        @media (max-width: 320px) {
          height: 40px;
        }
      }
    }

    .l-login-error {
      font-size: 0.75rem;
      color: $TOMATO100;
      text-align: center;
      margin-top: -4vh;
      margin-bottom: 1vh;
      white-space: pre;
    }

    .l-reset-success {
      font-size: 0.75rem;
      color: $BASIL100;
      text-align: center;
      margin-top: -4vh;
      margin-bottom: 1vh;
      white-space: pre;
    }

    .l-footer-container {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      margin-top: 15vh;

      .logo {
        width: 184px;
        height: 21px;

        @media (max-width: 420px) {
          width: 120px;
          height: 15px;
        }

        @media (max-width: 320px) {
          width: 100px;
          height: 12px;
        }
      }
    }
  }
}
</style>
